/**
 * AES-256-GCM for request/response payloads, matching the backend's
 * `PayloadCipher.java`.
 *
 * Wire format is a single base64 string of `IV || ciphertext || tag`, with a
 * fresh 12-byte IV per message prepended. Built on the browser's native
 * SubtleCrypto rather than a library: it is hardware-accelerated, adds nothing
 * to the bundle, and avoids depending on an unmaintained JS crypto package for
 * something on every request.
 *
 * ⚠️ **What this is.** It keeps payloads out of the Network tab, out of
 * intercepting proxies, out of browser extensions and out of traffic logs. It
 * is *not* a boundary against the signed-in user — the key is in this bundle,
 * the plaintext exists in memory either side of the call, and the data is
 * rendered into the DOM anyway. Anyone willing to open DevTools and set a
 * breakpoint sees everything. Same honest framing as `token-store.ts`: a
 * deterrent layered on top of TLS, never a substitute for the backend's
 * authorisation checks.
 *
 * SubtleCrypto is only exposed on secure origins (https, or localhost). On a
 * plain-http host `crypto.subtle` is undefined; `isAvailable()` reports that so
 * callers fall back to plaintext instead of failing every request.
 */

const IV_LENGTH = 12;
const TAG_BITS = 128;

/** Envelope field name; must match ENVELOPE_FIELD in PayloadEncryptionFilter. */
export const ENVELOPE_FIELD = 'd';

/** Request/response header marking an encrypted body. Must match ENC_HEADER. */
export const ENC_HEADER = 'X-Enc';

export class PayloadCipher {
  /** Imported lazily and cached — importKey is async and called on every request. */
  private keyPromise: Promise<CryptoKey> | null = null;

  constructor(private readonly base64Key: string) {}

  static isAvailable(): boolean {
    return (
      typeof globalThis.crypto !== 'undefined' &&
      typeof globalThis.crypto.subtle !== 'undefined'
    );
  }

  /** True when a syntactically usable 32-byte key is present. */
  isConfigured(): boolean {
    if (!this.base64Key || !PayloadCipher.isAvailable()) {
      return false;
    }
    try {
      return this.rawKey().length === 32;
    } catch {
      return false;
    }
  }

  private rawKey(): Uint8Array {
    const binary = atob(this.base64Key.trim());
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  private key(): Promise<CryptoKey> {
    if (!this.keyPromise) {
      this.keyPromise = crypto.subtle.importKey(
        'raw',
        this.rawKey(),
        { name: 'AES-GCM' },
        false,
        ['encrypt', 'decrypt'],
      );
    }
    return this.keyPromise;
  }

  async encrypt(plaintext: string): Promise<string> {
    const key = await this.key();
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const sealed = new Uint8Array(
      await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv, tagLength: TAG_BITS },
        key,
        new TextEncoder().encode(plaintext),
      ),
    );

    const out = new Uint8Array(iv.length + sealed.length);
    out.set(iv, 0);
    out.set(sealed, iv.length);
    return this.toBase64(out);
  }

  async decrypt(base64Payload: string): Promise<string> {
    const key = await this.key();
    const raw = this.fromBase64(base64Payload);
    if (raw.length <= IV_LENGTH) {
      throw new Error('Encrypted payload too short');
    }
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: raw.slice(0, IV_LENGTH), tagLength: TAG_BITS },
      key,
      raw.slice(IV_LENGTH),
    );
    return new TextDecoder().decode(plain);
  }

  /**
   * Chunked rather than `String.fromCharCode(...bytes)` — spreading a large
   * payload blows the argument limit and throws on big responses.
   */
  private toBase64(bytes: Uint8Array): string {
    let binary = '';
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
    }
    return btoa(binary);
  }

  private fromBase64(value: string): Uint8Array {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}
